import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Award, Users, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import PartnerLogos from "@/components/home/PartnerLogos";
import RelatedContentCarousel from "@/components/RelatedContentCarousel";

const ICONS = { award: Award, users: Users, handshake: Handshake };

export const ELITE_SUPPLIER_DEFAULT_DATA = {
  breadcrumb_label: "Antera Exchange",
  breadcrumb_href: "/exchange",
  hero_eyebrow: "The Elite Supplier Program",
  hero_title_prefix: "Antera Elite",
  hero_title_accent: "Supplier Program",
  hero_tagline: "The Antera Elite Supplier Program recognizes preferred supplier partners who deliver the highest level of technology integration, automation, service, and collaboration within the Antera Advance platform. These suppliers are committed to creating a seamless experience for distributors by investing in modern integrations, accurate data, and exceptional support.",
  benefit_sections: [
    {
      icon: "award", accent: "bg-sky-600", test_id_prefix: "elite-supplier-capability",
      eyebrow: "What Sets Them Apart",
      title: "Elite Supplier Capabilities",
      subtitle: "Elite Suppliers typically provide:",
      items: [
        "PromoStandards integration for live pricing, inventory, product data, order status, and shipment tracking.",
        "Real-time inventory and pricing updates directly within Advance.",
        "Electronic purchase order processing with automated acknowledgments.",
        "Artwork and proof workflow integration, where available.",
        "Automatic order status and shipment updates without manual intervention.",
        "Reliable APIs with high uptime and responsive technical support.",
        "Accurate product content, inventory, and pricing data.",
        "Collaborative customer support between the supplier and Antera.",
        "Ongoing participation in product enhancements and integration improvements that benefit mutual customers.",
      ],
    },
    {
      icon: "users", accent: "bg-indigo-600", test_id_prefix: "elite-supplier-distributor",
      eyebrow: "For Distributors",
      title: "Benefits for Distributors",
      subtitle: "Distributors who purchase from Antera Elite Suppliers receive more than just superior technology—they gain access to exclusive business advantages, including:",
      items: [
        "Special pricing and preferred distributor discounts",
        "Exclusive supplier incentives and promotional offers",
        "Supplier-funded rebates and reward programs",
        "Priority customer support from participating suppliers",
        "Faster order processing",
        "Fewer manual touchpoints and reduced administrative work",
        "Greater order accuracy",
        "Better inventory visibility with real-time availability",
        "Live order status and shipment tracking",
        "Improved customer service through increased automation",
        "A more efficient, end-to-end order lifecycle",
      ],
    },
    {
      icon: "handshake", accent: "bg-emerald-600", test_id_prefix: "elite-supplier-supplier",
      eyebrow: "For Suppliers",
      title: "Benefits for Suppliers",
      subtitle: "Elite Suppliers receive premium recognition and increased exposure throughout the Antera ecosystem, including:",
      items: [
        "Preferred positioning within the Antera Advance platform",
        "Increased visibility to thousands of distributors using Advance",
        "Recognition as a technology and automation leader",
        "Reduced support costs through automated workflows",
        "Stronger partnerships with Antera and its distributor community",
        "Opportunities to participate in joint marketing, promotional campaigns, webinars, and customer events",
        "Increased distributor engagement through exclusive pricing, incentive, and rebate programs",
      ],
    },
  ],
  closing: {
    // Rendered as: "<lead> <bold1> <middle> <bold2>"
    lead: "The Antera Elite Supplier Program is designed to create a",
    bold_1: "true partnership",
    middle: "between Antera, suppliers, and distributors—combining best-in-class technology with exclusive commercial benefits that help everyone",
    bold_2: "operate more efficiently, sell more effectively, and grow together.",
    primary_label: "Become an Elite Supplier",
    primary_href: "/contact",
    secondary_label: "Talk to Antera about the program",
    secondary_href: "/contact",
  },
  related: {
    tag: "Supplier Insights",
    title: "Elite Supplier Insights",
    subtitle: "Inside the brands we partner with — the latest stories from our Elite Suppliers.",
  },
};

function BenefitList({ items, accent, testIdPrefix }) {
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
      {items.map((text, i) => (
        <li key={text} data-testid={`${testIdPrefix}-${i}`} className="flex items-start gap-3 group">
          <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-full ${accent} flex items-center justify-center`}>
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </span>
          <span className="text-sm lg:text-[15px] text-slate-700 leading-relaxed">{text}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionHeader({ eyebrow, title, subtitle, Icon }) {
  return (
    <div className="max-w-3xl mx-auto text-center mb-10 lg:mb-14">
      {Icon && (
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white mb-4 shadow-lg shadow-sky-500/20">
          <Icon className="h-5 w-5" />
        </div>
      )}
      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-sky-600">{eyebrow}</p>
      <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{title}</h2>
      {subtitle && <p className="mt-4 text-base lg:text-lg text-slate-500 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

export default function EliteSupplierPage({ data }) {
  const d = { ...ELITE_SUPPLIER_DEFAULT_DATA, ...(data || {}) };
  const sections = d.benefit_sections || [];
  const closing = { ...ELITE_SUPPLIER_DEFAULT_DATA.closing, ...(d.closing || {}) };
  const related = { ...ELITE_SUPPLIER_DEFAULT_DATA.related, ...(d.related || {}) };

  return (
    <div data-testid="elite-supplier-page" className="page-transition">
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-16 bg-white relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-96 h-96 bg-sky-100/50 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-[28rem] h-[28rem] bg-indigo-100/50 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {d.breadcrumb_label ? (
              <Link to={d.breadcrumb_href || "/exchange"} data-testid="elite-supplier-back-link" className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 mb-6">
                <ArrowLeft className="h-3.5 w-3.5" /> {d.breadcrumb_label}
              </Link>
            ) : null}
            {d.hero_eyebrow ? (
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-sky-600 mb-3">{d.hero_eyebrow}</span>
            ) : null}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              {d.hero_title_prefix}{" "}
              <span className="bg-gradient-to-r from-sky-500 to-indigo-600 bg-clip-text text-transparent">{d.hero_title_accent}</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">{d.hero_tagline}</p>
          </motion.div>
        </div>
      </section>

      {sections.map((s, i) => {
        const Icon = ICONS[s.icon] || Award;
        const bg = i % 2 === 0 ? "bg-slate-50/60" : "bg-white";
        return (
          <section key={i} className={`py-14 lg:py-20 ${bg}`}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <SectionHeader eyebrow={s.eyebrow} title={s.title} subtitle={s.subtitle} Icon={Icon} />
              <BenefitList items={s.items} accent={s.accent} testIdPrefix={s.test_id_prefix || `elite-section-${i}`} />
            </div>
          </section>
        );
      })}

      <section className="py-14 lg:py-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute top-0 left-[15%] w-72 h-72 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 right-[10%] w-96 h-96 bg-indigo-400/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <p className="text-lg lg:text-xl text-slate-200 leading-relaxed font-light">
            {closing.lead}{" "}
            <span className="text-white font-semibold">{closing.bold_1}</span>{" "}
            {closing.middle}{" "}
            <span className="text-white font-semibold">{closing.bold_2}</span>
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to={closing.primary_href || "/contact"}>
              <Button data-testid="elite-supplier-become-btn" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 h-12 text-sm font-semibold">
                {closing.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to={closing.secondary_href || "/contact"}>
              <Button variant="outline" data-testid="elite-supplier-demo-btn" className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white rounded-full px-7 h-12 text-sm font-semibold">
                {closing.secondary_label}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="py-8">
        <PartnerLogos />
      </div>

      <RelatedContentCarousel
        tag={related.tag}
        title={related.title}
        subtitle={related.subtitle}
        testId="elite-supplier-related"
      />
    </div>
  );
}
